import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    IconButton,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StyledTextboxInput } from '../../shared/Components/StyledTextboxInput';
import type { PlayerRosterEntry } from '@moment-golf/types';
import { allPlayerDataFields } from '../../shared/config/PlayerDataFields';

interface AddPlayerDialogProps {
    open: boolean;
    onClose: () => void;
    onAdd: (entry: Omit<PlayerRosterEntry, 'index'>) => void;
    appTheme: ReturnType<typeof import('../../shared/Settings/appTheme').setAppTheme>;
    factor: number;
    /** Field keys from the roster template — only these fields are shown in the form */
    templateKeys: string[];
    teeSetOptions?: string[];
    divisionOptions?: string[];
    flightOptions?: string[];
}

const CORE_KEYS = ['playerName', 'playerEmail', 'playerPhone'];
const NUMBER_KEYS = new Set(['playerHandicap', 'approxCourseRating', 'approxLength', 'rewardPoints']);
const fieldMap = new Map(allPlayerDataFields.map(f => [f.key, f]));

export const AddPlayerDialog = ({
    open,
    onClose,
    onAdd,
    appTheme,
    factor,
    templateKeys,
    teeSetOptions = [],
    divisionOptions = [],
    flightOptions = [],
}: AddPlayerDialogProps) => {
    const [values, setValues] = useState<Record<string, string>>({});

    useEffect(() => {
        if (open) setValues({});
    }, [open]);

    const set = (key: string, value: string) => setValues(v => ({ ...v, [key]: value }));

    const handleClose = () => onClose();

    const handleAdd = () => {
        const name = (values.playerName ?? '').trim();
        const email = (values.playerEmail ?? '').trim();
        if (!name || !email) {
            window.alert('Player Name and Email are required.');
            return;
        }

        const phone = (values.playerPhone ?? '').trim();
        const entry: Omit<PlayerRosterEntry, 'index'> = {
            playerName: name,
            playerEmail: email,
            userID: null,
            ...(phone ? { playerPhone: phone } : {}),
        };

        for (const key of templateKeys.filter(k => !CORE_KEYS.includes(k))) {
            const raw = values[key] ?? '';
            if (raw === '') continue;
            (entry as any)[key] = NUMBER_KEYS.has(key) ? Number(raw) : raw;
        }

        onAdd(entry);
        handleClose();
    };

    // Extra template fields (everything in the template beyond name + email)
    const extraFields = templateKeys
        .filter(k => !CORE_KEYS.includes(k))
        .map(k => fieldMap.get(k))
        .filter(Boolean) as typeof allPlayerDataFields;

    const renderField = (def: typeof allPlayerDataFields[number]) => {
        const value = values[def.key] ?? '';

        if (def.type === 'select') {
            let options: string[] = def.options ?? [];
            if (def.eventSource === 'teeSet') options = teeSetOptions;
            if (def.eventSource === 'division') options = divisionOptions;
            if (def.eventSource === 'flight') options = flightOptions;
            if (options.length === 0) return null;

            return (
                <FormControl key={def.key} size='small' fullWidth>
                    <InputLabel>{def.label}</InputLabel>
                    <Select
                        value={value}
                        label={def.label}
                        onChange={(e) => set(def.key, e.target.value as string)}
                    >
                        <MenuItem value=''>—</MenuItem>
                        {options.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        return (
            <StyledTextboxInput
                key={def.key}
                label={def.label}
                type={def.type === 'number' ? 'number' : def.type === 'tel' ? 'tel' : def.type === 'email' ? 'email' : 'text'}
                value={value}
                onChange={(e) => set(def.key, e.target.value)}
                appTheme={appTheme}
                factor={factor}
            />
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                Add Player
                <IconButton size='small' onClick={handleClose} aria-label='Close'>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    <StyledTextboxInput
                        label='Player Name'
                        value={values.playerName ?? ''}
                        onChange={(e) => set('playerName', e.target.value)}
                        appTheme={appTheme}
                        factor={factor}
                        required
                    />
                    <StyledTextboxInput
                        label='Email'
                        type='email'
                        value={values.playerEmail ?? ''}
                        onChange={(e) => set('playerEmail', e.target.value)}
                        appTheme={appTheme}
                        factor={factor}
                        required
                    />
                    <StyledTextboxInput
                        label='Phone'
                        type='tel'
                        value={values.playerPhone ?? ''}
                        onChange={(e) => set('playerPhone', e.target.value)}
                        appTheme={appTheme}
                        factor={factor}
                    />
                    {extraFields.map(def => renderField(def))}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 1.5 }}>
                <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button variant='contained' onClick={handleAdd} sx={{ textTransform: 'none' }}>
                    Add Player
                </Button>
            </DialogActions>
        </Dialog>
    );
};
